// code contract - contribution selector represents either per paycehck/daily or per recurring schedule/daily, we should just have this be a display based component and receive the above as props.
import React from "react";
import type { ApolloClient } from "@apollo/client";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Col, Row, Form } from "react-bootstrap";
import {
  camelCase,
  filter,
  findIndex,
  get,
  isEmpty,
  isNil,
  lowerCase,
  max,
  min,
  size,
} from "lodash";
import classnames from "classnames";
import moment from "moment";

import Button from "components/Button";
import Alert from "components/Alert";
import SelectContributionBox from "./SelectContributionBox";

import { formatAmount, formatCurrency } from "utils/number";
import {
  DAILY,
  MONTHLY,
  payrollPeriodsToEnglishMapping,
  PER_PAY_PERIOD,
} from "utils/timeHelper";
import { getDailyToRecurringAmount } from "services/contributionService";
import { accountTypesToEnglishMapping } from "statics/accountTypes";
import "./Contributions.scss";

// Mirrors `recommendedContributionType` from statics/propTypes (the propTypes
// shape this component was validated against). `other` is treated as a plain
// array throughout (spread/filter/map with no null guards), so it is typed
// non-nullable here to match the component's runtime assumptions.
interface RecommendedContribution {
  recommended: number;
  other: number[];
  isMaxContributionOption?: boolean;
}

interface ContributionSchedule {
  frequency: string;
  dayOne?: string;
  anchorPayDate?: string;
}

interface ContributionAccount {
  id: string;
  accountType: string;
}

interface ModifierProps {
  onSelect?: (name?: string) => void;
  name?: string;
  label?: string;
  isSelected?: boolean;
}

const Modifier = ({ isSelected, name, label, onSelect }: ModifierProps) => {
  return (
    <div
      className={classnames("modifier", {
        selected: isSelected,
      })}
      onClick={() => onSelect!(name)}
    >
      {label || name}
    </div>
  );
};

function getDefaultOption(
  currentAmount: number | undefined,
  recommendations: RecommendedContribution
) {
  const isRecommended =
    currentAmount === recommendations.recommended || !currentAmount;

  if (isRecommended) {
    return "recommended";
  }

  const matchingSubOptionIdx = findIndex(
    recommendations.other,
    (other) => other === currentAmount
  );

  if (matchingSubOptionIdx >= 0) {
    return `other.${matchingSubOptionIdx}`;
  }

  return null;
}

const PAYCHECK_LIMITS: Record<string, { max: number; min: number }> = {
  MONTHLY: { max: 1000, min: 25 },
  TWICE_PER_MONTH: { max: 500, min: 13 },
  EVERY_OTHER_WEEK: { max: 500, min: 13 },
  WEEKLY: { max: 250, min: 6 },
  DAILY: { max: 30, min: 1 },
};

interface ContributionSelectorOwnProps {
  isSubmitting?: boolean;
  schedule: ContributionSchedule;
  error?: string;
  bankAccountNumber?: string;
  dailyMax?: number;
  weeklyMax?: number;
  monthlyMax?: number;
  everyOtherWeekMax?: number;
  perPayPeriodMax?: number;
  currentContributionAmount?: number;
  dailyRecommendations?: RecommendedContribution;
  weeklyRecommendations?: RecommendedContribution;
  everyOtherWeekRecommendations?: RecommendedContribution;
  monthlyRecommendations?: RecommendedContribution;
  perPayPeriodRecommendations?: RecommendedContribution;
  onSubmit?: (
    amount: number,
    isDaily: boolean,
    selectedAccountId: string
  ) => void;
  children?: React.ReactNode;
  isPaycheck?: boolean;
  isEditing?: boolean;
  showAccountSelector?: boolean;
  accounts?: ContributionAccount[];
  defaultAccountId?: string;
  // Dynamic per-frequency recommendation lookups
  // (e.g. `${camelCase(frequency)}Recommendations`) index these props by
  // computed string keys, so allow string indexing into the recommendations.
  [key: `${string}Recommendations`]: RecommendedContribution | undefined;
}

type ContributionSelectorProps = WithApolloClient<ContributionSelectorOwnProps>;

interface ContributionSelectorState {
  selectedOption: string | null;
  selectedCustom: boolean;
  showCustomBox: boolean;
  modifier: string;
  // Uses an empty string to clear the input between modifier screens, so this
  // holds either the numeric contribution amount or an empty string.
  customAmount: number | string | undefined;
  customAmountError: string;
  isFetchingCustomAmount: boolean;
  calculatedCustomAmount: number | null;
  showConfirmation: boolean;
  selectedAccountId: string;
  accountSelectionError: string | null;
}

class ContributionSelector extends React.PureComponent<
  ContributionSelectorProps,
  ContributionSelectorState
> {
  static defaultProps = {
    isPaycheck: false,
    showAccountSelector: false,
    accounts: [],
  };

  constructor(props: ContributionSelectorProps) {
    super(props);

    const selectedOption = getDefaultOption(
      props.currentContributionAmount,
      (this.props.isPaycheck
        ? props.perPayPeriodRecommendations
        : props[
            `${camelCase(this.props.schedule.frequency)}Recommendations`
          ]) as RecommendedContribution
    );

    const defaultModifier = selectedOption
      ? DAILY
      : this.props.schedule.frequency;

    this.state = {
      selectedOption,
      selectedCustom: !selectedOption,
      showCustomBox: !selectedOption,
      modifier: props.isPaycheck ? PER_PAY_PERIOD : defaultModifier,
      // Uses an empty string to clear the input between modifier screens
      customAmount: !selectedOption ? this.props.currentContributionAmount : "",
      customAmountError: "",
      isFetchingCustomAmount: false,
      calculatedCustomAmount: null,
      showConfirmation: false,
      selectedAccountId: this._getSelectedAccountId(),
      accountSelectionError: null,
    };
  }

  _getSelectedAccountId = () => {
    const { accounts, defaultAccountId } = this.props;

    if (defaultAccountId) {
      return defaultAccountId;
    }

    if (accounts && accounts.length === 1) {
      return accounts[0].id;
    }
    return "";
  };

  _getAllRecommendations = () => {
    const isDaily = this.state.modifier === DAILY;

    const frequencyString = isDaily
      ? "daily"
      : camelCase(this.props.schedule.frequency);

    const recs = (
      this.props.isPaycheck && !isDaily
        ? this.props.perPayPeriodRecommendations
        : this.props[`${frequencyString}Recommendations`]
    ) as RecommendedContribution;

    const contributionLimits = this._getContributionLimits();
    const recsWithinLimits = filter(
      [recs.recommended, ...recs.other],
      (rec) => {
        return rec >= contributionLimits.min;
      }
    );

    return recsWithinLimits;
  };

  _getCustomPositionToRecommended = () => {
    const recs = this._getAllRecommendations();
    const minRec = min(recs);
    const maxRec = max(recs);

    const { customAmount } = this.state;
    // customAmount can be a string ("" between screens) or number; the original
    // relies on JS coercion in these comparisons — preserved verbatim.
    // @ts-expect-error customAmount is string | number; comparison coerces at runtime
    if (customAmount! < minRec!) return "less_than_recommended";
    // @ts-expect-error customAmount is string | number; comparison coerces at runtime
    if (customAmount! > maxRec!) return "greater_than_recommended";
    return "within_recommended";
  };

  _handleSubmit = () => {
    if (
      this.props.showAccountSelector &&
      this.props.accounts &&
      size(this.props.accounts) > 1 &&
      isNil(this.state.selectedAccountId)
    ) {
      this.setState({
        accountSelectionError: "Please select a target account",
      });
      return;
    }

    const isDaily = this.state.modifier === DAILY;
    const baseTrackingObject = {
      isDaily,
      payrollFrequency: this.props.schedule.frequency,
    };

    if (this.state.selectedCustom) {
      const contributionLimits = this._getContributionLimits();
      const customIsValid =
        // @ts-expect-error customAmount is string | number; comparison coerces at runtime
        this.state.customAmount! >= contributionLimits.min &&
        !this.state.customAmountError;

      if (customIsValid) {
        window.analytics.track("User selected custom deduction amount", {
          ...baseTrackingObject,
          positionComparedToRecommended: this._getCustomPositionToRecommended(),
          contributionAmount: +this.state.customAmount!,
        });
        this.props.onSubmit!(
          +this.state.customAmount!,
          isDaily,
          this.state.selectedAccountId
        );
        return;
      } else {
        return;
      }
    }

    const recs = this.props.isPaycheck
      ? this.props.perPayPeriodRecommendations
      : this.props[
          `${camelCase(this.props.schedule.frequency)}Recommendations`
        ];
    const contributionAmount = +get(recs, this.state.selectedOption!);

    window.analytics.track("User selected preset deduction amount", {
      ...baseTrackingObject,
      selectedOption: this.state.selectedOption,
      contributionAmount,
    });

    this.props.onSubmit!(
      contributionAmount,
      false,
      this.state.selectedAccountId
    );
  };

  _updateModifier = (modifier?: string) => {
    this.setState({
      modifier: modifier!,
      customAmount: "",
      customAmountError: "",
    });
  };

  _getRecommendedContributions = (): RecommendedContribution => {
    if (this.state.modifier === DAILY) {
      return this.props.dailyRecommendations as RecommendedContribution;
    }
    if (this.props.isPaycheck) {
      return this.props[
        "perPayPeriodRecommendations"
      ] as RecommendedContribution;
    }
    return this.props[
      `${camelCase(this.props.schedule.frequency)}Recommendations`
    ] as RecommendedContribution;
  };

  _getModifier = () => {
    if (this.state.modifier === DAILY) {
      return this.state.modifier;
    }
    if (this.props.isPaycheck) {
      return PER_PAY_PERIOD;
    }
    return this.props.schedule.frequency;
  };

  _getSelectBoxes = () => {
    const recommendedContributions = this._getRecommendedContributions();
    const contributionLimits = this._getContributionLimits();

    const selectedRec = get(
      recommendedContributions,
      this.state.selectedOption!,
      null
    );

    const modifier = this._getModifier();

    const preferredSelectBox = (
      // SelectContributionBox does not declare `showAsMax` and also requires
      // `contributionLimits`; the original passes the former and omits the
      // latter for the preferred box — both preserved as-is.
      <SelectContributionBox
        key="preferred"
        amount={+recommendedContributions.recommended}
        isRecommended={true}
        // @ts-expect-error showAsMax is undeclared by SelectContributionBox; passed as in the original
        showAsMax={recommendedContributions.isMaxContributionOption}
        modifier={modifier}
        isSelected={
          !this.state.selectedCustom &&
          selectedRec === recommendedContributions.recommended
        }
        onSelect={() => this._setSelectedContribution("recommended")}
      />
    );

    const otherOptions = recommendedContributions.other
      .filter((val) => val >= contributionLimits.min)
      .map((val, idx) => (
        // contributionLimits is required by SelectContributionBox but the
        // original omits it for the non-custom boxes — preserved as-is.
        // @ts-expect-error contributionLimits omitted by design for preset boxes
        <SelectContributionBox
          key={val}
          amount={+val}
          modifier={modifier}
          isSelected={!this.state.selectedCustom && selectedRec === val}
          onSelect={() => this._setSelectedContribution(`other.${idx}`)}
        />
      ));

    const customInputBox = this.state.showCustomBox ? (
      <SelectContributionBox
        key="custom"
        amount={+this.state.customAmount!}
        customAmountError={this.state.customAmountError}
        modifier={modifier}
        isCustomAmount={true}
        isSelected={this.state.selectedCustom}
        onSelect={() => this.setState({ selectedCustom: true })}
        setCustomAmount={this._setCustomAmount}
        contributionLimits={contributionLimits}
      />
    ) : null;

    return [preferredSelectBox, ...otherOptions, customInputBox];
  };

  _getContributionLimits = () => {
    const scheduleLimit = PAYCHECK_LIMITS[this.props.schedule.frequency];
    let min;

    if (this.props.isEditing) {
      min = 0;
    } else {
      min =
        this.state.modifier === DAILY
          ? PAYCHECK_LIMITS.DAILY.min
          : scheduleLimit.min;
    }

    return {
      min,
      max:
        this.state.modifier === DAILY
          ? PAYCHECK_LIMITS.DAILY.max
          : scheduleLimit.max,
    };
  };

  _setCustomAmount = (amt: number) => {
    let customAmountError = "";
    const contributionLimits = this._getContributionLimits();

    if (amt < contributionLimits.min) {
      customAmountError = `Custom amount must be greater than or equal to ${formatCurrency(
        contributionLimits.min
      )}`;
    } else if (amt > contributionLimits.max) {
      customAmountError = `Custom amount must be less than or equal to ${formatCurrency(
        contributionLimits.max
      )}`;
    }
    this.setState({
      customAmount: formatAmount(amt),
      customAmountError,
    });
  };

  _setSelectedContribution = (selectedOption: string) => {
    this.setState({
      selectedCustom: false,
      selectedOption,
      customAmount: "",
      customAmountError: "",
    });
  };

  _buildConfirmationSentence = () => {
    const { frequency, anchorPayDate } = this.props.schedule;

    let amount: number | string | null | undefined;

    if (this.state.selectedCustom) {
      amount =
        this.state.modifier === DAILY
          ? this.state.calculatedCustomAmount
          : this.state.customAmount;
    } else {
      amount = get(
        this.props[`${camelCase(frequency)}Recommendations`],
        this.state.selectedOption!
      ) as number | undefined;
    }

    let firstPaymentDate = anchorPayDate;
    if (frequency === MONTHLY) {
      const dayOne =
        this.props.schedule.dayOne === "End of Month"
          ? 31
          : this.props.schedule.dayOne;

      /* The monthly scheduling logic takes a bit to calculate, basically we ask the user for the day of month they want to process
         so to figure out next processing date we have to figure out if that day already passed this month if not just use current month as processing date
         otherwise we have to figure out which date it will happen next month. The logic also accounts for variable end of month dates using moment in a way that
         ensures it will always default to last day of month if day number is outside of that months range.
      */
      const anchorDate = moment(anchorPayDate);
      // this format is important because it ensures that if we are in February that it will convert Feb 31 into Feb 28 vs March 3rd
      const currentMonthProcessingDate = moment()
        .date(dayOne as number)
        .month(anchorDate.month())
        .year(anchorDate.year());
      const willProcessThisMonth = anchorDate.isBefore(
        currentMonthProcessingDate.startOf("day")
      );

      let nextProcessingDate = currentMonthProcessingDate;

      if (!willProcessThisMonth) {
        const nextMonth = currentMonthProcessingDate.add(1, "months");
        nextProcessingDate = moment()
          .date(dayOne as number)
          .month(nextMonth.month())
          .year(nextMonth.year());
      }

      firstPaymentDate = nextProcessingDate.format("YYYY-MM-DD");
    }
    const startSentence = `starting ${firstPaymentDate}`;
    return (
      <p className="modal-text">
        By clicking continue, I authorize Icon Financial Services to debit my
        account ending in {this.props.bankAccountNumber} the amount of{" "}
        {formatCurrency(amount as number)}{" "}
        {lowerCase(payrollPeriodsToEnglishMapping[frequency])} {startSentence}.
        Scheduled payments may be cancelled up to 1 day in advance.
      </p>
    );
  };

  _getConfirmationMessage = () => {
    return (
      <div>
        <p>Confirm recurring contribution</p>
        {this._buildConfirmationSentence()}
        {this.props.error && <Alert type="error" msg={this.props.error} />}
        <div className="submit-row">
          <Button
            btnLabel="go back"
            color="cancel"
            name="cancel"
            onClick={() => this.setState({ showConfirmation: false })}
          />
          <Button
            btnLabel="submit"
            name="submit"
            withArrow
            loading={this.props.isSubmitting}
            onClick={this._handleSubmit}
          />
        </div>
      </div>
    );
  };

  _getRecurringAmount = async () => {
    this.setState({ isFetchingCustomAmount: true });
    const customAmount = await getDailyToRecurringAmount(
      this.props.client as ApolloClient<object>,
      {
        frequency: this.props.schedule.frequency,
        amount: +this.state.customAmount!,
      }
    );
    this.setState({
      isFetchingCustomAmount: false,
      calculatedCustomAmount: customAmount,
    });

    this.setState({ showConfirmation: true });
  };

  _handleAccountChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    this.setState({
      selectedAccountId: e.target.value,
      accountSelectionError: null,
    });
  };

  _getContent = () => {
    if (this.state.showConfirmation) {
      return this._getConfirmationMessage();
    }

    const name = this.props.isPaycheck
      ? PER_PAY_PERIOD
      : this.props.schedule.frequency;
    const label = this.props.isPaycheck
      ? "PER PAY PERIOD"
      : payrollPeriodsToEnglishMapping[this.props.schedule.frequency];
    const isSelected =
      this.state.modifier === this.props.schedule.frequency ||
      this.state.modifier === PER_PAY_PERIOD;
    const frequencyModifier = (
      <Modifier
        name={name}
        label={label}
        onSelect={this._updateModifier}
        isSelected={isSelected}
      />
    );

    let handleSubmit;
    const isDailyCustom =
      this.state.modifier === DAILY && this.state.selectedCustom;

    if (this.props.isPaycheck) {
      handleSubmit = this._handleSubmit;
    } else if (isDailyCustom) {
      handleSubmit = this._getRecurringAmount;
    } else {
      handleSubmit = () => this.setState({ showConfirmation: true });
    }

    return (
      <>
        {this.props.children}
        {this.props.showAccountSelector &&
          this.props.accounts &&
          size(this.props.accounts) > 1 && (
            <div className="account-selector-container">
              {this.state.accountSelectionError && (
                <Alert type="error" msg={this.state.accountSelectionError} />
              )}
              <Form.Group className="mb-3" controlId="accountSelector">
                <Form.Label>Select Target Account</Form.Label>
                <Form.Select
                  value={this.state.selectedAccountId}
                  onChange={this._handleAccountChange}
                  isInvalid={!!this.state.accountSelectionError}
                >
                  <option value="" disabled>
                    Select an account
                  </option>
                  {this.props.accounts.map((account) => (
                    <option value={account.id} key={account.id}>
                      {
                        // accountType is a runtime string; index the fixed-key
                        // mapping via a string-indexable view.
                        (
                          accountTypesToEnglishMapping as Record<string, string>
                        )[account.accountType]
                      }
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {this.state.accountSelectionError}
                </Form.Control.Feedback>
              </Form.Group>
            </div>
          )}
        <div>
          <div className="modifier-picker">
            <Modifier
              name={DAILY}
              onSelect={this._updateModifier}
              isSelected={this.state.modifier === DAILY}
            />

            {frequencyModifier}
          </div>
        </div>
        <div className="select-contributions-container">
          <Row>
            <Col> {this._getSelectBoxes()}</Col>
          </Row>
        </div>

        <div className="submit-row" style={{ paddingTop: 20 }}>
          {this.props.error && <Alert type="error" msg={this.props.error} />}

          {!this.state.showCustomBox && (
            <span
              style={{
                alignSelf: "center",
              }}
              onClick={() =>
                this.setState({ showCustomBox: true, selectedCustom: true })
              }
              className="icon-link"
            >
              Enter Custom Amount
            </span>
          )}
          <Button
            onClick={handleSubmit}
            btnLabel="Submit"
            name="submit"
            withArrow={true}
            loading={
              this.props.isSubmitting || this.state.isFetchingCustomAmount
            }
            disabled={
              this.state.selectedCustom &&
              (!isEmpty(this.state.customAmountError) ||
                isNil(this.state.customAmount) ||
                this.state.customAmount === "")
            }
          />
        </div>
      </>
    );
  };

  render() {
    return (
      <div className="contribution-setup-wrapper">{this._getContent()}</div>
    );
  }
}

export default withApollo<ContributionSelectorOwnProps>(ContributionSelector);
